<template>
  <div class="thai-stocks">
    <!-- ===============================
         ACTION CARD (ของเดิม)
    =============================== -->
    <v-card class="mb-4 action-card">
      <v-card-title class="d-flex align-center justify-space-between">
        <span>เลือกวัน </span>

        <select v-model="selectedDate" class="date-select">
          <option v-for="d in calendarItems" :key="d" :value="d">
            {{ d }}
          </option>
        </select>
      </v-card-title>

      <v-card-text>
        <div class="d-flex align-center gap-2 action-row">
          <v-btn
            color="primary"
            :loading="loading"
            :disabled="!selectedDate || loading"
            @click="loadData"
          >
            Load
          </v-btn>

          <v-btn variant="outlined" :disabled="loading" @click="clearAll">
            Clear
          </v-btn>

          <v-select
            v-model="totalValueFilter"
            :items="totalValueOptions"
            density="comfortable"
            variant="outlined"
            hide-details
            class="volume-filter"
          />

          <v-select
            v-model="stockGroupFilter"
            :items="stockGroupOptions"
            variant="outlined"
            hide-details
            class="group-select"
          />

          <v-btn
            variant="outlined"
            color="warning"
            :loading="snapshotStore.loading"
            :disabled="!selectedDate || snapshotStore.loading"
            @click="snapshotStore.fetchSnapshot(selectedDate)"
          >
            ราคาล่าสุด
          </v-btn>
          <v-btn
            size="small"
            color="primary"
            :disabled="!canApplyRsr"
            @click="applyRsr"
          >
            Apply RSR
          </v-btn>
        </div>
      </v-card-text>
    </v-card>

    <v-range-slider
      v-model="rsrRange"
      :min="0"
      :max="dates.length - 1"
      :step="1"
      show-ticks="always"
      thumb-label="always"
    >
      <template #thumb-label="{ modelValue }">
        {{ formatHeaderDate(dates[modelValue]) }}
      </template>

      <template #tick-label="{ tick }">
        <small>{{ formatHeaderDate(dates[tick.value]) }}</small>
      </template>
    </v-range-slider>

    <!-- ===============================
         MARKET TABLE
    =============================== -->
    <v-card v-if="dates.length">
      <v-card-text class="market-wrapper">
        <!-- ===============================
             TABLE TOP (HEADER ROW MODEL)
        =============================== -->
        <div
          ref="topScroll"
          class="table-top-scroll table-x-scroll"
          :style="{ '--col-count': headerCols.length + rsrColCount }"
          @scroll="syncScroll"
        >
          <div
            v-for="row in topRows"
            :key="row.symbol"
            class="matrix-row header"
          >
            <!-- SYMBOL -->
            <div class="cell symbol">
              {{ row.symbol }}
            </div>

            <template v-for="d in headerCols" :key="d">
              <!-- RSR HEADER (ก่อน date) -->
              <div
                v-if="hasRsrBeforeCol(d)"
                class="cell date rsr-col clickable"
                @click="setSort('RSR')"
              >
                RSR
                <span v-if="sortKey === 'RSR'" class="sort-indicator">
                  {{ sortOrder === "desc" ? "↓" : "↑" }}
                </span>
              </div>

              <!-- ORIGINAL DATE / LIVE / TICK -->
              <div
                class="cell date"
                :class="{
                  'sorted-col': sortKey === d,
                  'live-col': d === LIVE_COL,
                  'tick-col': d === TICK_COL,
                }"
                @click="d !== TICK_COL && setSort(d)"
              >
                <template v-if="d === LIVE_COL">
                  <span class="live-badge">LIVE</span>
                </template>

                <template v-else-if="d === TICK_COL">
                  <span class="tick-badge">กี่ช่อง</span>
                </template>

                <template v-else>
                  {{ formatHeaderDate(d) }}
                  <span v-if="sortKey === d" class="sort-indicator">
                    {{ sortOrder === "desc" ? "↓" : "↑" }}
                  </span>
                </template>
              </div>
            </template>
          </div>
        </div>

        <!-- ===============================
            TABLE BODY
        =============================== -->
        <div
          ref="bodyScroll"
          class="table-body-scroll table-x-scroll"
          @scroll="syncScroll"
        >
          <div
            class="market-matrix"
            :style="{ '--col-count': headerCols.length + rsrColCount }"
          >
            <template v-for="row in filteredAndSortedRows" :key="row.symbol">
              <!-- ================= MAIN ROW ================= -->
              <div class="matrix-row">
                <!-- SYMBOL -->
                <div
                  class="cell symbol clickable"
                  @click="toggleExpand(row.symbol)"
                >
                  <span
                    class="symbol-text"
                    :class="{ 'symbol-biglot': hasBigLotInWindow(row) }"
                  >
                    {{ row.symbol }}
                  </span>
                </div>

                <!-- DATE + LIVE + TICK -->
                <template v-for="d in headerCols" :key="d">
                  <!-- ===== RSR CELL (INSERT BEFORE DATE) ===== -->
                  <div v-if="hasRsrBeforeCol(d)" class="cell gain rsr-col">
                    {{ rsrStore.getActiveValue(row.symbol) ?? "-" }}
                  </div>

                  <!-- ===== ORIGINAL CELL ===== -->
                  <div
                    class="cell gain"
                    :class="[
                      d !== LIVE_COL && d !== TICK_COL
                        ? gainClass(row.gains?.[d])
                        : '',
                      {
                        'sorted-col': sortKey === d,
                        'live-col': d === LIVE_COL,
                        'tick-col': d === TICK_COL,
                      },
                    ]"
                  >
                    <!-- ===== LIVE ===== -->
                    <template v-if="d === LIVE_COL">
                      <template v-if="snapshotStore.getBySymbol(row.symbol)">
                        <span
                          :class="snapshotGainClass(
                    snapshotStore.getBySymbol(row.symbol)!.gain
                  )"
                        >
                          {{
                            formatGain(
                              snapshotStore.getBySymbol(row.symbol)!.gain
                            )
                          }}
                        </span>
                      </template>
                      <template v-else>-</template>
                    </template>

                    <!-- ===== TICK ===== -->
                    <template v-else-if="d === TICK_COL">
                      <template v-if="getTickValue(row) !== null">
                        <span :class="tickClass(getTickValue(row)!)">
                          {{ getTickValue(row) }}
                        </span>
                      </template>
                      <template v-else>-</template>
                    </template>

                    <!-- ===== NORMAL EOD ===== -->
                    <template v-else>
                      {{ formatGain(row.gains?.[d]) }}
                    </template>
                  </div>
                </template>
              </div>

              <!-- ================= EXPAND PANEL ================= -->
              <div v-if="expandedSymbol === row.symbol" class="expand-panel">
                <!-- TOTAL VALUE -->
                <div class="expand-row">
                  <div class="cell expand-label">Total Value</div>

                  <template v-for="d in headerCols" :key="d">
                    <!-- RSR PLACEHOLDER -->
                    <div v-if="hasRsrBeforeCol(d)" class="cell expand-cell">
                      -
                    </div>

                    <!-- MAIN CELL -->
                    <div class="cell expand-cell">
                      <template v-if="d === LIVE_COL">
                        <template v-if="snapshotStore.getBySymbol(row.symbol)">
                          {{
                            formatMV(
                              snapshotStore.getBySymbol(row.symbol)!.total_value
                            )
                          }}
                        </template>
                        <template v-else>-</template>
                      </template>

                      <template v-else>
                        {{ formatMV(row.total_values?.[d]) }}
                      </template>
                    </div>
                  </template>
                </div>

                <!-- OPEN -->
                <div class="expand-row">
                  <div class="cell expand-label">Open</div>

                  <template v-for="d in headerCols" :key="d">
                    <div v-if="hasRsrBeforeCol(d)" class="cell expand-cell">
                      -
                    </div>

                    <div class="cell expand-cell">
                      <template v-if="d === LIVE_COL">
                        <template v-if="snapshotStore.getBySymbol(row.symbol)">
                          {{
                            fmt2(snapshotStore.getBySymbol(row.symbol)!.open)
                          }}
                        </template>
                        <template v-else>-</template>
                      </template>

                      <template v-else>
                        {{ fmt2(row.open?.[d]) }}
                      </template>
                    </div>
                  </template>
                </div>

                <!-- HIGH -->
                <div class="expand-row">
                  <div class="cell expand-label">High</div>

                  <template v-for="d in headerCols" :key="d">
                    <div v-if="hasRsrBeforeCol(d)" class="cell expand-cell">
                      -
                    </div>

                    <div class="cell expand-cell">
                      <template v-if="d === LIVE_COL">
                        <template v-if="snapshotStore.getBySymbol(row.symbol)">
                          {{
                            fmt2(snapshotStore.getBySymbol(row.symbol)!.high)
                          }}
                        </template>
                        <template v-else>-</template>
                      </template>

                      <template v-else>
                        {{ fmt2(row.high?.[d]) }}
                      </template>
                    </div>
                  </template>
                </div>

                <!-- CLOSE -->
                <div class="expand-row">
                  <div class="cell expand-label">Close</div>

                  <template v-for="d in headerCols" :key="d">
                    <div v-if="hasRsrBeforeCol(d)" class="cell expand-cell">
                      -
                    </div>

                    <div class="cell expand-cell">
                      <template v-if="d === LIVE_COL">
                        <template v-if="snapshotStore.getBySymbol(row.symbol)">
                          {{
                            fmt2(snapshotStore.getBySymbol(row.symbol)!.close)
                          }}
                        </template>
                        <template v-else>-</template>
                      </template>

                      <template v-else>
                        {{ fmt2(row.close?.[d]) }}
                      </template>
                    </div>
                  </template>
                </div>

                <!-- LOW -->
                <div class="expand-row">
                  <div class="cell expand-label">Low</div>

                  <template v-for="d in headerCols" :key="d">
                    <div v-if="hasRsrBeforeCol(d)" class="cell expand-cell">
                      -
                    </div>

                    <div class="cell expand-cell">
                      <template v-if="d === LIVE_COL">
                        <template v-if="snapshotStore.getBySymbol(row.symbol)">
                          {{ fmt2(snapshotStore.getBySymbol(row.symbol)!.low) }}
                        </template>
                        <template v-else>-</template>
                      </template>

                      <template v-else>
                        {{ fmt2(row.low?.[d]) }}
                      </template>
                    </div>
                  </template>
                </div>

                <!-- BIG LOT -->
                <div class="expand-row">
                  <div class="cell expand-label">Big Lot</div>

                  <template v-for="d in headerCols" :key="d">
                    <div v-if="hasRsrBeforeCol(d)" class="cell expand-cell">
                      -
                    </div>

                    <div
                      class="cell expand-cell"
                      :class="
                        d === LIVE_COL
                          ? trValueClass(
                              snapshotStore.getBySymbol(row.symbol)?.tr_value
                            )
                          : trValueClass(row.tr_value?.[d])
                      "
                    >
                      <template v-if="d === LIVE_COL">
                        <template v-if="snapshotStore.getBySymbol(row.symbol)">
                          {{
                            formatMV(
                              snapshotStore.getBySymbol(row.symbol)!.tr_value
                            )
                          }}
                        </template>
                        <template v-else>-</template>
                      </template>

                      <template v-else>
                        {{ formatMV(row.tr_value?.[d]) }}
                      </template>
                    </div>
                  </template>
                </div>
              </div>
            </template>
          </div>
        </div>

        <!-- ===============================
     SET INDEX TABLE BODY
=============================== -->
        <div
          ref="indexScroll"
          class="table-body-scroll table-x-scroll"
          @scroll="syncScroll"
        >
          <div
            class="market-matrix"
            :style="{ '--col-count': headerCols.length + rsrColCount }"
          >
            <!-- ================= GAIN (FIX) ================= -->
            <div class="matrix-row">
              <div class="cell symbol index-symbol">
                <strong>SET</strong>
              </div>

              <template v-for="d in headerCols" :key="d">
                <!-- ===== RSR CELL (BEFORE DATE) ===== -->
                <div v-if="hasRsrBeforeCol(d)" class="cell gain rsr-col">
                  {{ "-" }}
                </div>

                <!-- ===== ORIGINAL GAIN CELL ===== -->
                <div
                  class="cell gain"
                  :class="indexChangeClass(setIndexByDate.get(d)?.gain)"
                >
                  {{ setIndexByDate.get(d)?.gain ?? "-" }}
                </div>
              </template>
            </div>

            <!-- ================= PIVOT LOW (FIX) ================= -->
            <div class="matrix-row">
              <div
                class="cell symbol clickable index-symbol"
                @click="setIndexExpanded = !setIndexExpanded"
              >
                Pivot Low
              </div>

              <template v-for="d in headerCols" :key="d">
                <!-- RSR PLACEHOLDER (KEEP ALIGNMENT) -->
                <div v-if="hasRsrBeforeCol(d)" class="cell">-</div>

                <!-- ORIGINAL PIVOT LOW -->
                <div class="cell">
                  {{ setIndexByDate.get(d)?.pivotLow ?? "-" }}
                </div>
              </template>
            </div>

            <!-- ================= EXPAND ================= -->
            <div v-if="setIndexExpanded" class="expand-panel">
              <div
                v-for="row in setIndexTableRows.slice(2)"
                :key="row.label"
                class="expand-row"
              >
                <div class="cell expand-label">
                  {{ row.label }}
                </div>

                <template v-for="d in headerCols" :key="d">
                  <!-- RSR PLACEHOLDER -->
                  <div v-if="hasRsrBeforeCol(d)" class="cell expand-cell">
                    -
                  </div>

                  <!-- ORIGINAL VALUE -->
                  <div class="cell expand-cell">
                    {{ row.values[headerCols.indexOf(d)] ?? "-" }}
                  </div>
                </template>
              </div>
            </div>
          </div>
        </div>
      </v-card-text>
    </v-card>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, nextTick, watch } from "vue";
import { useThaiStockStore } from "@/store/modules/thaistock.store";
import { useThaiStockSnapshotStore } from "@/store/modules/thaistockSnapshot.store";
import { useSetIndexStore } from "@/store/modules/setindex.store";
import { useRsrStore } from "@/store/modules/rsr.store";

import type { SetIndexCandle } from "@/store/modules/setindex.store";

import { STOCK_GROUP_MAP, type StockGroupKey } from "@/constants/stock-groups";

const thaiStockstore = useThaiStockStore();
const snapshotStore = useThaiStockSnapshotStore();
const setIndexStore = useSetIndexStore();

const stockGroupFilter = ref<StockGroupKey>("ALL");
const selectedDate = ref<string>("");

let calendarItems: string[] = [];

const rsrStore = useRsrStore();
// const rsrStartDate = ref<string | null>(null)
// const rsrEndDate = ref<string | null>(null)
const currentRsrEndDate = ref<string | null>(null);

const dates = ref<string[]>([]);
const rows = ref<Row[]>([]);
type SortKey = string | "RSR";
const sortKey = ref<SortKey>("");
const sortOrder = ref<"asc" | "desc">("desc");
const expandedSymbol = ref<string | null>(null);
const VIEW_BEFORE = 8;
const VIEW_AFTER = 4;

async function runRsr(start: string, end: string) {
  await rsrStore.fetch(start, end)
}

function hasRsrBeforeCol(d: string) {
  if (d === LIVE_COL || d === TICK_COL) return false
  return rsrStore.activeRange?.end === d
}

const rsrColCount = computed(() => {
  return rsrStore.activeRange ? 1 : 0
})

// slider range (index-based)
const rsrRange = ref<[number, number]>([0, 0]);

// ========== helpers ==========
function formatDateLabel(d?: string) {
  if (!d) return "-";
  const dt = new Date(d);
  return dt.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
  });
}

// ========== map index -> date ==========
const rsrStartDate = computed(() => dates.value[rsrRange.value[0]]);

const rsrEndDate = computed(() => dates.value[rsrRange.value[1]]);

const canApplyRsr = computed(() => {
  return (
    !!rsrStartDate.value &&
    !!rsrEndDate.value &&
    rsrStartDate.value !== rsrEndDate.value
  );
});

async function applyRsr() {
  const start = rsrStartDate.value;
  const end = rsrEndDate.value;

  if (!start || !end || start === end) return;

  await runRsr(start, end);

  // mark active
  // activeRsrEndDate.value = end
}

// ========== default behavior ==========
watch(
  dates,
  (d) => {
    if (!d.length) return;

    const end = d.length - 1;
    rsrRange.value = [Math.max(0, end - 1), end];
  },
  { immediate: true }
);

onMounted(async () => {
  await setIndexStore.fetchCalendar();

  // await runRsr("2025-12-29", "2026-01-08");

  if (Array.isArray(setIndexStore.calendar)) {
    calendarItems = setIndexStore.calendar.slice();
  }

  if (calendarItems.length > 1) {
    selectedDate.value = calendarItems[calendarItems.length - 2];
  }
});

async function loadData(): Promise<void> {
  if (!selectedDate.value) return;
  await thaiStockstore.loadEodWindow(
    selectedDate.value,
    VIEW_BEFORE,
    VIEW_AFTER
  );
  buildTable();
  await nextTick();
  scrollToRight();
}

function scrollToRight() {
  const targets = [topScroll.value, bodyScroll.value, indexScroll.value];

  for (const el of targets) {
    if (!el) continue;
    el.scrollLeft = el.scrollWidth - el.clientWidth;
  }

  // header ใช้ transform (ถ้าคุณทำ follower)
  // if (topScroll.value) {
  //   const x =
  //     bodyScroll.value?.scrollWidth! - bodyScroll.value?.clientWidth!
  //   topScroll.value.style.transform = `translateX(${-x}px)`
  // }
}

function clearAll(): void {
  thaiStockstore.clearFrame();
  rows.value = [];
  dates.value = [];
}

const loading = thaiStockstore.loading;
const error = thaiStockstore.error;

const stockGroupOptions = [
  { title: "ทั้งหมด", value: "ALL" },
  { title: "SET50", value: "SET50" },
  { title: "SET100", value: "SET100" },
  { title: "SSET", value: "SSET" },
  { title: "MAI", value: "MAI" },
];

const LIVE_COL = "__LIVE__";
const TICK_COL = "__TICK__";

const headerCols = computed(() => {
  if (!snapshotStore.hasSnapshot) {
    return dates.value;
  }

  return [...dates.value, LIVE_COL, TICK_COL];
});

/* =========================
   TOP TABLE ROW MODEL
========================= */

// -------------------------
// SET INDEX (fixed rows, no expand)
// -------------------------
const SET_INDEX_SYMBOL = "SETINDEX";

type FixedIndexRow = {
  label: string;
  // text for EOD date columns only
  text: Record<string, string>;
  // optional: for coloring the cell (only makes sense for %gain row)
  gains?: Record<string, number>;
  // optional: shown only in LIVE_COL
  liveText?: string | null;
  liveClass?: string;
  // optional: shown only in TICK_COL
  tickText?: string | null;
  tickClass?: string;
};

const setIndexExpanded = ref(false);

const setIndexByDate = computed(() => {
  const map = new Map<string, SetIndexCandle>();
  for (const c of setIndexStore.setIndexCandles) {
    map.set(c.trade_date, c);
  }
  return map;
});

const fmt2 = (v: number | null | undefined) =>
  typeof v === "number" ? v.toFixed(2) : "-";

const setIndexTableRows = computed(() => {
  if (!headerCols.value.length) return [];

  return [
    {
      label: "Gain",
      values: headerCols.value.map((d) => {
        const c = setIndexByDate.value.get(d);
        return c?.gain == null ? "-" : formatIndexChange(c.gain);
      }),
    },
    {
      label: "Pivot Low",
      values: headerCols.value.map((d) => {
        const c = setIndexByDate.value.get(d);
        return c?.pivotLow ?? "-";
      }),
    },
    {
      label: "High",
      values: headerCols.value.map((d) =>
        fmt2(setIndexByDate.value.get(d)?.high)
      ),
    },
    {
      label: "Close",
      values: headerCols.value.map((d) =>
        fmt2(setIndexByDate.value.get(d)?.close)
      ),
    },
    {
      label: "Low",
      values: headerCols.value.map((d) =>
        fmt2(setIndexByDate.value.get(d)?.low)
      ),
    },
  ];
});

const topRows = computed(() => [
  {
    symbol: "SYMBOL",
  },
]);

const topScroll = ref<HTMLElement | null>(null);
const bodyScroll = ref<HTMLElement | null>(null);
const indexScroll = ref<HTMLElement | null>(null);

let isSyncing = false;

function syncScroll(e: Event) {
  if (isSyncing) return;
  isSyncing = true;

  const src = e.target as HTMLElement;
  const x = src.scrollLeft;

  if (topScroll.value && src !== topScroll.value) {
    topScroll.value.scrollLeft = x;
  }
  if (bodyScroll.value && src !== bodyScroll.value) {
    bodyScroll.value.scrollLeft = x;
  }
  if (indexScroll.value && src !== indexScroll.value) {
    indexScroll.value.scrollLeft = x;
  }

  requestAnimationFrame(() => {
    isSyncing = false;
  });
}

// function formatHeaderDate(d: string): string {
//   const [y, m, day] = d.split("-");
//   return `${day}/${m}/${y.slice(2)}`;
// }

function formatHeaderDate(d?: string) {
  if (!d) return "-";
  const [y, m, day] = d.split("-");
  return `${day}/${m}`;
}

function trValueClass(v?: number) {
  return typeof v === "number" && v > 0 ? "tr-active" : "";
}

function formatMV(v: number | undefined): string {
  if (v == null || isNaN(v)) return "-";

  if (v >= 1_000_000_000) return (v / 1_000_000_000).toFixed(1) + "B";
  if (v >= 1_000_000) return (v / 1_000_000).toFixed(1) + "M";
  if (v >= 100_000) return (v / 1_000_000).toFixed(1) + "M";
  if (v >= 1_000) return (v / 1_000).toFixed(0) + "K";
  return v.toFixed(0);
}

function formatPrice(v: number | undefined): string {
  if (v == null || isNaN(v)) return "-";
  return v.toFixed(2);
}

function formatGain(v: number | undefined): string {
  if (v == null || isNaN(v)) return "-";
  if (v === 0) return "0%";
  const sign = v > 0 ? "+" : "";
  return `${sign}${v.toFixed(2)}%`;
}

function snapshotGainClass(v: number) {
  if (v > 0) return "snapshot-up";
  if (v < 0) return "snapshot-down";
  return "snapshot-flat";
}

const totalValueOptions = [
  { title: "ไม่กรอง", value: 0 },
  { title: "> 1M", value: 1_000_000 },
  { title: "> 2M", value: 2_000_000 },
  { title: "> 3M", value: 3_000_000 },
  { title: "> 5M", value: 5_000_000 },
  { title: "> 10M", value: 10_000_000 },
  { title: "> 20M", value: 20_000_000 },
];

const totalValueFilter = ref(2_000_000);

function frameCount(): number {
  const frame = thaiStockstore.getFrame();
  return Array.isArray(frame) ? frame.length : 0;
}

type Row = {
  symbol: string;
  gains: Record<string, number>;
  total_values: Record<string, number>;
  open: Record<string, number>;
  high: Record<string, number>;
  close: Record<string, number>;
  low: Record<string, number>;
  tr_value: Record<string, number>;
};

function buildViewDates(
  allDates: string[],
  centerDate: string,
  before = 5,
  after = 5
): string[] {
  const idx = allDates.indexOf(centerDate);
  if (idx === -1) return [];

  const start = Math.max(0, idx - before);
  const end = Math.min(allDates.length - 1, idx + after);

  return allDates.slice(start, end + 1);
}

function buildTable() {
  const frame = thaiStockstore.getFrame();
  if (!Array.isArray(frame) || frame.length === 0) return;
  if (!selectedDate.value) return;

  const dateSet = new Set<string>();
  for (const r of frame) {
    dateSet.add(r.date);
  }

  // 🔹 ALL dates (ไว้คำนวณ indicator)
  const allDates = Array.from(dateSet).sort(
    (a, b) => new Date(a).getTime() - new Date(b).getTime()
  );

  // 🔹 VIEW dates (ไว้ render table)
  dates.value = buildViewDates(
    allDates,
    selectedDate.value,
    VIEW_BEFORE,
    VIEW_AFTER
  );

  const map = new Map<
    string,
    {
      gains: Record<string, number>;
      total_values: Record<string, number>;
      open: Record<string, number>;
      high: Record<string, number>;
      close: Record<string, number>;
      low: Record<string, number>;
      tr_value: Record<string, number>;
    }
  >();

  for (const r of frame) {
    if (!map.has(r.symbol)) {
      map.set(r.symbol, {
        gains: {},
        total_values: {},
        open: {},
        high: {},
        close: {},
        low: {},
        tr_value: {},
      });
    }

    const row = map.get(r.symbol)!;

    // ⚠️ เก็บข้อมูลครบทุกวัน (แม้ไม่ render)
    if (typeof r.gain === "number") row.gains[r.date] = r.gain;
    if (typeof r.total_value === "number")
      row.total_values[r.date] = r.total_value;
    if (typeof r.open === "number") row.open[r.date] = r.open;
    if (typeof r.high === "number") row.high[r.date] = r.high;
    if (typeof r.close === "number") row.close[r.date] = r.close;
    if (typeof r.low === "number") row.low[r.date] = r.low;
    if (typeof r.tr_value === "number") row.tr_value[r.date] = r.tr_value;
  }

  rows.value = Array.from(map.entries()).map(([symbol, data]) => ({
    symbol,
    gains: data.gains,
    total_values: data.total_values,
    open: data.open,
    high: data.high,
    close: data.close,
    low: data.low,
    tr_value: data.tr_value,
  }));

  sortKey.value = selectedDate.value;
}

const filteredAndSortedRows = computed(() => {
  const key = sortKey.value;
  const minValue = totalValueFilter.value;
  const group = stockGroupFilter.value;

  let result = rows.value;

  /* =========================
     FILTER: STOCK GROUP
  ========================= */
  if (group !== "ALL") {
    const set = STOCK_GROUP_MAP[group];
    result = result.filter((row) => set.has(row.symbol));
  }

  /* =========================
     FILTER: total_value
     - EOD  -> row.total_values
     - LIVE -> snapshot.total_value
  ========================= */
  /* =========================
   FILTER: total_value
   - ใช้ selectedDate (หรือ end date) เป็นฐาน
========================= */
/* =========================
   FILTER: total_value
   - LIVE  -> snapshot.total_value
   - EOD   -> active RSR end_date หรือ selectedDate
========================= */
if (minValue > 0) {
  result = result.filter((row) => {
    // ===== LIVE =====
    const snap = snapshotStore.getBySymbol(row.symbol)
    if (snap && typeof snap.total_value === "number") {
      if (snap.total_value >= minValue) return true
    }

    // ===== EOD =====
    const refDate =
      rsrStore.activeRange?.end ?? selectedDate.value

    if (!refDate) return false

    const tv = row.total_values?.[refDate]
    return typeof tv === "number" && tv >= minValue
  })
}


  /* =========================
     SORT
  ========================= */
  if (key) {
    result = [...result].sort((a, b) => {
      let av = -Infinity;
      let bv = -Infinity;

      // ===== SORT BY LIVE =====
      if (key === LIVE_COL) {
        const sa = snapshotStore.getBySymbol(a.symbol);
        const sb = snapshotStore.getBySymbol(b.symbol);

        av = typeof sa?.gain === "number" ? sa.gain : -Infinity;
        bv = typeof sb?.gain === "number" ? sb.gain : -Infinity;
      }

      // ===== SORT BY RSR (ACTIVE) =====
      else if (key === "RSR") {
        av = rsrStore.getActiveValue(a.symbol) ?? -Infinity;
        bv = rsrStore.getActiveValue(b.symbol) ?? -Infinity;
      }

      // ===== SORT BY NORMAL DATE =====
      else {
        av = typeof a.gains?.[key] === "number" ? a.gains[key] : -Infinity;
        bv = typeof b.gains?.[key] === "number" ? b.gains[key] : -Infinity;
      }

      return sortOrder.value === "desc" ? bv - av : av - bv;
    });
  }

  return result;
});

function expandCellValue(row: any, d: string) {
  if (d === LIVE_COL) {
    const s = snapshotStore.getBySymbol(row.symbol);
    return s ? formatMV(s.total_value) : "-";
  }

  return formatMV(row.total_values?.[d]);
}

function setSort(key: string) {
  if (sortKey.value === key) {
    sortOrder.value = sortOrder.value === "desc" ? "asc" : "desc";
  } else {
    sortKey.value = key;
    sortOrder.value = "desc";
  }
}

function toggleExpand(symbol: string) {
  expandedSymbol.value = expandedSymbol.value === symbol ? null : symbol;
}

function gainClass(v?: number) {
  if (v == null) return "";
  if (v > 0) return "gain-up";
  if (v < 0) return "gain-down";
  return "gain-flat";
}

function hasBigLotInWindow(row: Row): boolean {
  if (!selectedDate.value) return false;

  const idx = dates.value.indexOf(selectedDate.value);
  if (idx < 0) return false;

  const start = Math.max(0, idx - 3);
  const windowDates = dates.value.slice(start, idx + 1);

  return windowDates.some((d) => {
    const v = row.tr_value?.[d];
    return typeof v === "number" && v > 0;
  });
}

function getTickValue(row: Row): number | null {
  const snap = snapshotStore.getBySymbol(row.symbol);
  if (!snap) return null;

  const prevDate = dates.value[dates.value.length - 1];
  const prevLow = row.low?.[prevDate];

  if (!prevLow) return null;

  return calcTickDistance(snap.close, prevLow);
}

function calcTickDistance(
  snapshotClose: number,
  prevLow: number
): number | null {
  if (!snapshotClose || !prevLow) return null;

  const tick = getTickSize(prevLow);
  return Math.round((snapshotClose - prevLow) / tick);
}

function getTickSize(price: number): number {
  if (price < 2) return 0.01;
  if (price < 5) return 0.02;
  if (price < 10) return 0.05;
  if (price < 25) return 0.1;
  if (price < 100) return 0.25;
  if (price < 200) return 0.5;
  if (price < 400) return 1.0;
  return 2.0;
}

function tickClass(ticks: number | null | undefined): string {
  if (ticks == null) return "";

  if (ticks < 0) return "tick-break";
  if (ticks <= 3) return "tick-support";

  return "";
}

const formatIndexChange = (v?: number | null) => {
  if (v == null) return "-";
  const sign = v > 0 ? "+" : "";
  return `${sign}${v.toFixed(2)}`;
};
const indexChangeClass = (v?: number | null) => {
  if (v == null) return "";
  if (v > 0) return "gain-up";
  if (v < 0) return "gain-down";
  return "";
};
</script>

<!-- <style scoped>
.thai-stocks,
.market-wrapper {
  font-family: 'Inter', system-ui, -apple-system, BlinkMacSystemFont,
    'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
} -->

<style scoped>
/* ======================================================
   BASE
====================================================== */
.thai-stocks,
.market-wrapper {
  font-family: "Inter", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI",
    Roboto, Helvetica, Arial, sans-serif;
  padding: 0;
}

.market-wrapper {
  /* padding: 0; */
  padding-right: 20px;
}

/* ===============================
   ACTION CARD
=============================== */
.action-card {
  position: relative;
  z-index: 100;
}

.action-row {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap; /* mobile รองรับ */
}

.action-row .v-btn {
  height: 36px;
  min-height: 36px;
  padding: 0 14px;
  font-size: 13px;
  letter-spacing: 0.3px;
}

/* ======================================================
   GRID VARIABLES (SINGLE SOURCE)
   ✅ ต้องครอบคลุมทั้ง header และ body เพราะ header ไม่ได้ใช้ market-matrix
====================================================== */
.market-matrix,
.table-top-scroll {
  --symbol-col: 140px;
  --data-col: 96px;
}

/* ======================================================
   SCROLL CONTAINERS
====================================================== */

/* ✅ ตัว scroll-x ที่ใช้ร่วมกัน (top/body/index) */
.table-x-scroll {
  overflow-x: auto;
  overflow-y: hidden;
  width: 100%;
  scrollbar-gutter: stable; /* กันความกว้างเพี้ยนเวลามี/ไม่มี scrollbar */
}

/* ✅ body ต้องมี y-scroll */
.table-body-scroll {
  max-height: 65vh;
  overflow-y: auto;
  position: relative;
  scrollbar-gutter: stable; /* ช่วยให้ setindex ไม่เหลื่อมจาก scrollbar */
}

/* ✅ header: ห้าม y-scroll */
.table-top-scroll {
  background: #020617;
  position: sticky;
  top: 0;
  z-index: 10;
  overflow-y: hidden;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

/* ======================================================
   GRID CORE
   - header ใช้ .table-top-scroll .matrix-row
   - body/index ใช้ .market-matrix (container) + .matrix-row (cells)
====================================================== */

/* -------- BODY/INDEX CONTAINER -------- */
.market-matrix {
  /* ทำให้ grid กว้างพอที่จะ scroll */
  min-width: calc(var(--symbol-col) + (var(--col-count) * var(--data-col)));
}

/* -------- ROW GRID (ใช้กับทั้ง header และ body) -------- */
.table-top-scroll .matrix-row,
.market-matrix .matrix-row,
.market-matrix .expand-row {
  display: grid;
  grid-template-columns: var(--symbol-col) repeat(
      var(--col-count),
      var(--data-col)
    );
  align-items: center;

  /* ทำให้ row กว้างพอที่จะ scroll (สำคัญมากสำหรับ header) */
  min-width: calc(var(--symbol-col) + (var(--col-count) * var(--data-col)));
}

/* ======================================================
   CELL BASE
====================================================== */
.cell {
  padding: 7px 10px;
  white-space: nowrap;
  box-sizing: border-box;
  font-variant-numeric: tabular-nums;
}

/* header date cell */
.cell.date {
  text-align: center;
  font-weight: 600;
  color: #e2e8f0;
}

/* data cells */
.cell.gain,
.expand-cell {
  text-align: center;
}

/* ======================================================
   STICKY SYMBOL COLUMN
====================================================== */
.cell.symbol {
  position: sticky;
  left: 0;
  z-index: 40;

  width: var(--symbol-col);
  min-width: var(--symbol-col);
  max-width: var(--symbol-col);

  background: #020617;
  font-weight: 700;
  text-align: left;
}

/* ทุก cell ที่ไม่ใช่ symbol → จัดกลาง */
.cell:not(.symbol) {
  justify-content: center;
  text-align: center;
}

/* clickable symbol */
.clickable {
  cursor: pointer;
}

/* ======================================================
   COLORS
====================================================== */
.gain-up {
  color: #22c55e;
  font-weight: 600;
}
.gain-down {
  color: #ef4444;
  font-weight: 600;
}
.gain-flat {
  color: #cbd5f5;
}

.sorted-col {
  background: rgba(255, 255, 255, 0.08);
}

/* ======================================================
   LIVE / TICK STYLES
====================================================== */
.live-badge {
  display: inline-block;
  padding: 2px 6px;
  font-size: 11px;
  font-weight: 700;
  border-radius: 4px;
  background: #7c3aed;
  color: #fff;
}

.live-col {
  background: rgba(124, 58, 237, 0.12);
}

.tick-col {
  background: rgba(168, 85, 247, 0.12);
}

.tick-badge {
  background: #074272;
  color: #fff;
  font-size: 11px;
  padding: 2px 6px;
  border-radius: 4px;
  font-weight: 700;
}

.tick-support {
  background: #14532d;
  color: #22c55e;
  padding: 2px 6px;
  border-radius: 4px;
  font-weight: 700;
}

.tick-break {
  background: #450a0a;
  color: #1e51c0;
  padding: 2px 6px;
  border-radius: 4px;
  font-weight: 700;
}

/* snapshot gain colors */
.snapshot-up {
  color: #22c55e;
  font-weight: 700;
}
.snapshot-down {
  color: #ef4444;
  font-weight: 700;
}
.snapshot-flat {
  color: #cbd5f5;
  font-weight: 700;
}

/* ======================================================
   BIG LOT
====================================================== */
.tr-active {
  background: #7c3aed;
  color: #ffffff;
  font-weight: 700;
  border-radius: 4px;
}

.symbol-text {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 6px;
}

.symbol-text.symbol-biglot {
  background: linear-gradient(135deg, #7c3aed, #6d28d9);
  color: #ffffff;
  font-weight: 800;
}

/* ======================================================
   EXPAND PANEL
====================================================== */
.expand-panel {
  background: rgba(255, 255, 255, 0.04);
}

.expand-row:not(:last-child) {
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

/* second sticky label in expand */
.expand-label {
  position: sticky;
  left: 0;
  z-index: 30;

  width: var(--symbol-col);
  min-width: var(--symbol-col);
  max-width: var(--symbol-col);

  background: #020617;
  color: #94a3b8;
  text-align: right;
  padding-right: 12px;
  font-size: 12px;
  font-weight: 600;
}

/* ======================================================
   V-SELECT TWEAKS
====================================================== */
:deep(.volume-filter) {
  width: 110px;
  max-width: 100%;
  flex: 0 0 auto;
  --v-input-control-height: 28px;
  --v-input-padding-top: 0px;
  --v-field-padding-bottom: 0px;
}
:deep(.volume-filter .v-field__input) {
  padding: 2px 10px;
  font-size: 13px;
  line-height: 1.2;
}
:deep(.volume-filter .v-field--variant-outlined) {
  min-height: 28px;
}

:deep(.group-select) {
  width: 120px;
  max-width: 100%;
  flex: 0 0 auto;
  --v-input-control-height: 28px;
  --v-input-padding-top: 0px;
  --v-field-padding-bottom: 0px;
}
:deep(.group-select .v-field__input) {
  padding: 0px 10px;
  font-size: 13px;
  line-height: 1.5;
}
:deep(.group-select .v-field--variant-outlined) {
  min-height: 28px;
}

.cell.rsr-col {
  font-weight: 600;
  opacity: 0.9;
}

/* ======================================================
   MOBILE
====================================================== */
@media (max-width: 640px) {
  .market-wrapper,
  .action-row {
    font-size: 10px;
  }
  .market-matrix,
  .table-top-scroll {
    --symbol-col: 80px;
    --data-col: 55px;
  }

  .cell {
    padding: 2px 4px;
  }

  .action-row {
    gap: 2px;
  }

  :deep(.volume-filter) {
    width: 100px;
    min-width: 100px;
  }

  /* มือถือ: ลดความสูง body กัน setindex หลุดขอบล่าง */
  .table-body-scroll {
    max-height: 50vh;
  }
  .action-card {
    font-size: 10px;
  }

  .action-card .v-card-title {
    padding: 4px 6px;
    font-size: 10px;
  }

  .action-card .v-card-text {
    padding: 4px 4px;
  }

  .action-row {
    gap: 2px;
    flex-wrap: wrap; /* สำคัญมาก */
  }

  /* ---------- Buttons ---------- */
  .action-card .v-btn {
    font-size: 11px;
    min-height: 30px;
    padding: 0 8px;
  }

  /* ---------- v-select ---------- */
  .action-card .v-select {
    font-size: 11px;
    min-height: 30px;
  }

  .action-card .v-field__input {
    min-height: 30px;
    padding-top: 2px;
    padding-bottom: 2px;
  }

  /* ---------- native select ---------- */
  .action-card .date-select {
    font-size: 11px;
    padding: 2px 6px;
  }
    /* =========================
     RSR RANGE SLIDER (MOBILE)
  ========================= */

  .v-range-slider {
    font-size: 10px;
    margin-top: 4px;
    margin-bottom: 4px;
    margin-right:20px;
  }

  /* track */
  .v-slider-track {
    height: 3px;
  }

  /* ticks */
  .v-slider-track__ticks {
    height: 6px;
  }

  /* thumb */
  .v-slider-thumb {
    width: 14px;
    height: 14px;
  }

  /* thumb label */
  .v-slider-thumb__label {
    font-size: 10px;
    padding: 2px 2px;
    min-height: unset;
  }

  /* value text */
  .v-slider-thumb__label > span {
    line-height: 1;
  }
}
</style>
